import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { RegisterTeamMemberDto } from "../../../types/register-team-member.types";
import { TeamMembersAPI } from "../../../APIs/team_members.api";
import { useToast } from "../../../components/toast";
import { cleanObject } from "../../../utils/clean-object.utils";
import { PersonalInfoApiError } from "../../../types/personal_info.types";
import { EditableField } from "../../../components/editable-field";
import { UserRoles, UserRolesTranslations } from "../../../types/user.types";
import { TeamMemberForm } from "../../my-team/team-members-information/team-member-form";

export function TeamMemberAddPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const teamId = searchParams.get("teamId");

  const [formData, setFormData] = useState<
    RegisterTeamMemberDto & { role_id: number }
  >({
    personal_info: {
      dni: "",
      licence_number: "",
      first_name: "",
      last_name: "",
      birth_date: undefined,
      email: "",
      primary_phone_number: "",
      secondary_phone_number: "",
      emergency_contact_number: "",
      gender: undefined,
      blood_type: "",
    },
    addresses: [
      {
        address_line: "",
        city: "",
        state: "",
        country: "",
      },
    ],
    is_helmsman: false,
    role_id: UserRoles.PRIMARY_USER,
  });

  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [apiErrors, setApiErrors] = useState<PersonalInfoApiError>({
    dni: null,
    email: null,
    licence_number: null,
  });

  const handleChange = (e: React.ChangeEvent<HTMLElement>, index?: number) => {
    setApiErrors({
      dni: null,
      email: null,
      licence_number: null,
    });
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value, type } = target;

    setFormData((prev) => {
      if (!prev) return prev;

      if (type === "checkbox") {
        return {
          ...prev,
          [name]: (target as HTMLInputElement).checked,
        };
      }

      if (index !== undefined && prev.addresses) {
        return {
          ...prev,
          addresses: prev.addresses.map((address, i) =>
            i === index ? { ...address, [name]: value } : address
          ),
        };
      }

      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        const parentKey = parent as keyof (RegisterTeamMemberDto & {
          role_id: number;
        });
        const parentObject = prev[parentKey];

        if (parentObject && typeof parentObject === "object") {
          return {
            ...prev,
            [parent]: {
              ...parentObject,
              [child]: value,
            },
          };
        }

        return {
          ...prev,
          [parent]: {
            [child]: value,
          },
        };
      }

      return {
        ...prev,
        [name]: type === "number" ? Number(value) : value,
      };
    });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!teamId) {
      addToast({
        message: "Error: ID de equipo no encontrado",
        type: "error",
        location: "bottom-center",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { role_id, ...memberData } = formData;
      const data = cleanObject(memberData);

      const response = await TeamMembersAPI.registerTeamMemberAdmin(
        data as RegisterTeamMemberDto,
        parseInt(teamId),
        role_id
      );

      if (response.statusCode === 201) {
        addToast({
          message: "¡Miembro agregado al equipo!",
          type: "success",
          location: "bottom-center",
        });
        console.log("response", response);
        navigate(`/dashboard/new-credentials`, {
          state: {
            username: response.data.user.username,
            password: response.data.user.password,
            firstName: response.data.personal_info.first_name,
            lastName: response.data.personal_info.last_name,
            redirectUrl: `/dashboard/teams/view/${teamId}`,
          },
        });
      } else {
        const errorCode = response.additionalData?.errorCode;
        if (errorCode === "DNI_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            dni: "Este DNI ya está en uso.",
          }));
        } else if (errorCode === "EMAIL_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            email: "Este correo electrónico ya está en uso.",
          }));
        } else if (errorCode === "LICENSE_NUMBER_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            licence_number: "Este número de licencia ya está en uso.",
          }));
        }
        addToast({
          message: response.message,
          type: "error",
          location: "bottom-center",
          duration: 10000,
        });
      }
    } catch (error) {
      addToast({
        message: "Error al agregar miembro, intente nuevamente",
        type: "error",
        location: "bottom-center",
        duration: 10000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (teamId) {
      navigate(`/dashboard/teams/view/${teamId}`);
    } else {
      navigate(-1);
    }
  };

  if (!teamId) {
    return (
      <div className="min-h-screen mt-10">
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
          <div className="text-center text-red-600">
            Error: ID de equipo no encontrado
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mt-10">
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="flex space-x-2">
          <h2 className="text-2xl font-bold text-center text-gray-600">
            Agregar Miembro al Equipo
          </h2>
        </div>

        <div className="space-y-4 mt-6">
          <EditableField
            label="Rol del Miembro"
            value={formData.role_id}
            options={Object.entries(UserRolesTranslations)
              .slice(-2)
              .map(([key, value]) => ({ key, value }))}
            type="select"
            onChange={handleChange}
            name="role_id"
            placeholder="Seleccione el rol"
            required
          />

          <TeamMemberForm
            buttonText="Agregar"
            createTeamMember={formData}
            handleChange={handleChange}
            onSubmit={onSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            personalInfoApiError={apiErrors}
          />
        </div>
      </div>
    </div>
  );
}
